<script lang="ts" setup>
import {CatalogSearchConfigInterface} from "@/js/interfaces/catalogsearch"
import {RefreshIcon, SearchIcon} from '@heroicons/vue/outline'
import {onMounted, onUnmounted, ref} from "vue"
import {getSearchResults} from "@/js/api/sp"
import {useTracking} from "@/js/composables/plausible"
import {containsAttackChars, containsChinese, containsNonEuropeanChars} from "@/js/composables/sanitize";

const tracking = useTracking()

const props = defineProps<{
	config: CatalogSearchConfigInterface
}>()

const query = ref('')
const isLoading = ref(false)
const results = ref('')
const searchWrapper = ref<HTMLElement | null>(null)

// eslint-disable-next-line no-undef
let timer = 0 as unknown as NodeJS.Timeout

const closeSearch = () => {
	clearTimeout(timer)
	query.value = ''
	results.value = ''
	isLoading.value = false
}

const onClickOutside = (event: MouseEvent) => {
	if (searchWrapper.value && !searchWrapper.value.contains(event.target as Node)) {
		closeSearch()
	}
}

const onKeyDown = (event: KeyboardEvent) => {
	if (event.key === 'Escape') closeSearch()
}

onMounted(() => {
	document.addEventListener('mousedown', onClickOutside)
	document.addEventListener('keydown', onKeyDown)
})

onUnmounted(() => {
	document.removeEventListener('mousedown', onClickOutside)
	document.removeEventListener('keydown', onKeyDown)
})

const toResults = () => {
	if (query.value.length > props.config.query.min_length) {
		tracking.trackAction('Search', 'Results', query.value, 0)
		window.location.href = `${props.config.urls.result}?q=${encodeURIComponent(query.value)}`
	}
}

const autoComplete = async () => {
	clearTimeout(timer)

	if (query.value.length < props.config.query.min_length) {
		results.value = ''
		isLoading.value = false
		return
	}
	if (containsChinese(query.value)) {
		console.debug("Query contains Chinese characters. Aborting search.");
		return;
	}
	if (containsNonEuropeanChars(query.value)) {
		console.debug("Query contains non-Latin characters. Aborting search.");
		return;
	}
	if (containsAttackChars(query.value)) {
		console.debug("Query contains attack characters. Aborting search.");
		return;
	}
	try {
		if (query.value.length >= props.config.query.min_length) {
			timer = setTimeout(async () => {
				isLoading.value = true
				const sanitizedQuery = encodeURIComponent(query.value)
				results.value = await getSearchResults(sanitizedQuery, props.config)
				tracking.trackAction('Search', 'Autocomplete', query.value, 0)
				isLoading.value = false
			}, 600)
		}
	} catch (e) {
		isLoading.value = false
		console.error(e)
	}
}
</script>

<template>
	<div ref="searchWrapper">
		<div class="search-container">
			<input id="search"
			       v-model="query"
			       :maxlength="config.query.max_length"
			       autocomplete="off"
			       class="input-text"
			       placeholder="Veranstaltung suchen..."
			       type="search"
			       @input="autoComplete"
			       @keyup.enter="toResults"/>
			<RefreshIcon v-if="isLoading" class="refresh-icon spinning"/>
			<SearchIcon v-else class="search-icon" @click="toResults"/>
		</div>
		<div id="search_autocomplete" class="search-autocomplete" v-html="results"></div>
	</div>
</template>

<style scoped>
@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

.search-container {
	position: relative;
}

.search-icon {
	position: absolute;
	right: 10px;
	top: 50%;
	transform: translateY(-50%);
	width: 20px;
	height: 20px;
}

.refresh-icon {
	position: absolute;
	right: 10px;
	top: calc(50% - 10px); /* Adjust this value as needed */
	width: 20px;
	height: 20px;
}

.spinning {
	animation: spin 2s linear infinite;
}
</style>
